import { useState } from "react";
import axios from "axios";
import { ArrowRight, Linkedin, Mail } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      setDone(true);
      setForm({ name: "", email: "", company: "", message: "" });
      toast.success("Message sent. We’ll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <section className="container-x pt-12 md:pt-20 pb-12">
        <p className="eyebrow mb-8">Contact</p>
        <h1
          className="hero-headline text-[40px] sm:text-5xl md:text-7xl lg:text-[88px] max-w-[14ch]"
          data-testid="contact-headline"
        >
          Let’s build something inevitable.
        </h1>
      </section>

      <section className="border-t border-border">
        <div className="container-x py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Side info */}
          <aside className="md:col-span-4 space-y-10">
            <div>
              <p className="font-mono-label text-[11px] tracking-[0.22em] uppercase text-muted-foreground mb-3">
                Direct
              </p>
              <a
                href="mailto:info@samstella.com"
                data-testid="contact-email-link"
                className="inline-flex items-center gap-2 font-display text-2xl md:text-3xl tracking-tight link-underline"
              >
                <Mail size={20} /> info@samstella.com
              </a>
            </div>
            <div>
              <p className="font-mono-label text-[11px] tracking-[0.22em] uppercase text-muted-foreground mb-3">
                Social
              </p>
              <a
                href="https://www.linkedin.com/company/samstella-llc/"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-linkedin-link"
                className="inline-flex items-center gap-2 font-display text-2xl md:text-3xl tracking-tight link-underline"
              >
                <Linkedin size={20} /> SAMSTELLA LLC
              </a>
            </div>
            <div className="pt-6 border-t border-border">
              <p className="font-mono-label text-[11px] tracking-[0.22em] uppercase text-muted-foreground mb-4">
                What we love hearing about
              </p>
              <ul className="space-y-2 text-base font-light text-foreground/80">
                <li>— Commerce + media infrastructure</li>
                <li>— Live shopping + creator-led distribution</li>
                <li>— Retail media &amp; performance attribution</li>
                <li>— Strategic partnerships &amp; commercialization</li>
              </ul>
            </div>
          </aside>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            className="md:col-span-8 border border-border bg-background p-6 md:p-10"
            data-testid="contact-form"
          >
            {done && (
              <div
                className="mb-8 border border-accent p-5 bg-accent text-accent-foreground"
                data-testid="contact-success"
              >
                <p className="font-mono-label text-[11px] tracking-[0.22em] uppercase mb-2">
                  Message received
                </p>
                <p className="text-sm">Thanks for reaching out. We’ll respond shortly to the email you provided.</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Name" required>
                <input
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="contact-input-name"
                  className="input"
                  placeholder="Your full name"
                  autoComplete="name"
                  required
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="contact-input-email"
                  className="input"
                  placeholder="you@company.com"
                  autoComplete="email"
                  required
                />
              </Field>
              <Field label="Company" optional className="md:col-span-2">
                <input
                  type="text"
                  value={form.company}
                  onChange={update("company")}
                  data-testid="contact-input-company"
                  className="input"
                  placeholder="Company or platform"
                  autoComplete="organization"
                />
              </Field>
              <Field label="Message" required className="md:col-span-2">
                <textarea
                  value={form.message}
                  onChange={update("message")}
                  data-testid="contact-input-message"
                  className="input min-h-[180px] resize-y"
                  placeholder="A few sentences about what you’re building or exploring."
                  required
                />
              </Field>
            </div>

            <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
              <p className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                We respond to substantive inquiries within 1–2 business days.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Send Message"} <ArrowRight size={14} />
              </button>
            </div>

            <style>{`
              .input {
                width: 100%;
                background: transparent;
                border: 0;
                border-bottom: 1px solid hsl(var(--border));
                padding: 12px 0;
                font-family: 'Manrope', sans-serif;
                font-size: 15px;
                color: #0A0A0A;
                outline: none;
                transition: border-color 200ms ease;
              }
              .input::placeholder { color: hsl(var(--muted-foreground)); }
              .input:focus { border-bottom-color: #0A0A0A; }
            `}</style>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, required, optional, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono-label text-[10px] tracking-[0.22em] uppercase text-muted-foreground flex items-center gap-2 mb-1">
        {label}
        {required && <span className="text-foreground">*</span>}
        {optional && <span className="text-muted-foreground/70 normal-case tracking-normal">(optional)</span>}
      </span>
      {children}
    </label>
  );
}
